import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChevronIcon from '../icons/chevron-bottom';
import s from './styles.module.scss';
import CrossIcon from '../icons/crossIcon';
import BaseButton from '../baseButton';

interface ChartSessionTimerProps {
  sessionId: string | null;
  serverElapsedTime: number;
  rpmTime: number;
  ccmTime: number;
  className?: string;
  onClose: () => void;
}

const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const ChartSessionTimer: React.FC<ChartSessionTimerProps> = ({
  sessionId,
  serverElapsedTime,
  rpmTime,
  ccmTime,
  className,
  onClose,
}) => {
  const { t } = useTranslation();

  const [elapsedTime, setElapsedTime] = useState(0);
  const [expanded, setExpanded] = useState(false);

  // Sync with server elapsed time on every ping
  useEffect(() => {
    setElapsedTime(serverElapsedTime);
  }, [serverElapsedTime]);

  useEffect(() => {
    if (!sessionId) {
      setElapsedTime(0);
      return;
    }

    let interval: ReturnType<typeof setInterval> | null = null;

    const startInterval = () => {
      if (document.visibilityState !== 'visible') return;
      interval = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (interval) clearInterval(interval);
      } else {
        startInterval();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    startInterval();

    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sessionId]);

  if (!sessionId) {
    return null;
  }

  return (
    <div className={`${s.wrapper} ${className || ''}`}>
      <div className={`${s.drawer} ${expanded ? s.drawerOpen : ''}`}>
        <div className={s.breakdown}>
          <span className={s.breakdownLabel}>Total RPM Time:</span>
          <span className={s.breakdownTime}>{formatTime(rpmTime)}</span>
        </div>
        <div className={s.breakdown}>
          <span className={s.breakdownLabel}>Total CCM Time:</span>
          <span className={s.breakdownTime}>{formatTime(ccmTime)}</span>
        </div>
      </div>
      <div
        className={`${s.horizontalDivider} ${
          expanded ? s.horizontalDividerVisible : ''
        }`}
      />
      <div className={s.timerRow}>
        <div className={s.divider}>
          <span className={s.time}>{formatTime(elapsedTime)}</span>
          <BaseButton
            className={s['close-chart']}
            rightIcon={<CrossIcon width="20" height="20" />}
            onClick={onClose}
            type="danger"
            label={t('labels.closeChart')}
          />
        </div>
        <button
          className={`${s.btn} ${s.chevronBtn}`}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <div className={`${s.chevron} ${expanded ? s.chevronUp : ''}`}>
            <ChevronIcon width="20" height="20" />
          </div>
        </button>
      </div>
    </div>
  );
};

export default ChartSessionTimer;
